import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../../../../../components/Common/UI/Form';
import { Input } from '../../../../../../components/Common/UI/Input';
import { guidanceUser } from '../../../../../../contstant';
import { Button } from '../../../../../../components/Common/UI/Button';
import Icons from '../../../../../../components/Common/Icons/Icons';
import Separator from '../../../../../../components/Common/UI/Separator';
import { StepDirectionType } from '../index';
import { LogoFromSettings } from '../../../../Common/Logo';

const additionalNav = {
  row1: [
    { path: '#', label: 'Test your browser' },
    { path: '#', label: 'Trouble testing your browser' },
  ],
  row2: [
    { path: '#', label: 'Already with us?' },
    { path: '#', label: 'New Customer?' },
    { path: '#', label: 'New member' },
  ],
};

function AdditionalLinkItem({ path, label, withSeparator }: { path: string; label: string; withSeparator: boolean }) {
  return (
    <>
      <Link className="underline text-xs font-medium" to={path}>
        {label}
      </Link>
      {withSeparator ? <Separator className="h-auto w-0.5" orientation="vertical" /> : null}
    </>
  );
}

export interface ViewerData {
  id: string;
  name: string;
  email: string;
  firstName: string;
  lastName: string;
  username: string;
}

interface FormContentProps {
  handleStep: (direction?: StepDirectionType) => void;
}

function FormContent({ handleStep }: FormContentProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [viewer, setViewer] = useState<ViewerData | null>(null);

  useEffect(() => {
    const tempViewers = JSON.parse(localStorage.getItem('tempViewers') || '[]');
    const isAuthorizedUser = localStorage.getItem('isAuthorizedUser');

    if (isAuthorizedUser === 'true' && tempViewers.length > 0) {
      setViewer(tempViewers[0]);
      localStorage.removeItem('isAuthorizedUser');
    }
  }, []);

  const handleToggleShowPass = () => {
    setShowPassword(prevState => !prevState);
  };

  const targetUser = viewer || guidanceUser;
  const isViewerMode = !!viewer;

  const formSchema = z.object({
    username: z.string().refine(value => value === targetUser.username, {
      message: 'Incorrect username',
    }),
    password: z
      .string()
      .min(8, { message: 'Password must be at least 8 characters.' })
      .regex(/^(?=.*[0-9])(?=.*[\D])([\D0-9]+)$/, {
        message: 'Password should include numbers and letters.',
      })
      .regex(/[^A-Za-zА-Яа-яІЇҐЄіїґє0-9]|[A-ZА-ЯІЇҐЄ]/, {
        message: 'The password must contain at least one capital letter or special character',
      })
      .regex(/[a-zа-яіїґє]/, { message: 'Must contain one lowercase letter.' })
      .regex(/^\S+$/g, { message: 'Password must not contain spaces.' }),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      username: targetUser.username,
      password: '',
    },
  });

  useEffect(() => {
    if (viewer) {
      form.setValue('username', viewer.username);
    }
  }, [viewer, form]);

  const usernameWatch = form.watch('username');
  const passwordWatch = form.watch('password');

  function onSubmit(values: z.infer<typeof formSchema>) {
    handleStep('next');
  }

  return (
    <div id="content-card" className="flex flex-col gap-4 w-full">
      <div>
        <div id="card-header" className="md:hidden flex gap-3 items-center justify-center mb-4">
          <LogoFromSettings size="lg" />
        </div>

        {isViewerMode ? (
          <div className="mb-4">
            <h3 className="text-[28px] font-bold">
              Welcome, {viewer?.firstName} {viewer?.lastName}
            </h3>
            <p className="text-sm text-gray-600 mt-1">Authorized viewer Login</p>
          </div>
        ) : (
          <h3 className="text-[28px] font-bold">Log In</h3>
        )}
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4">
          <FormField
            name="username"
            render={({ field }) => (
              <FormItem>
                <FormLabel variant="guidance">Username</FormLabel>
                <FormControl>
                  <Input placeholder="Username" variant="transparent" sizeVariant="lg" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel variant="guidance">Password</FormLabel>
                <FormControl>
                  <Input
                    placeholder="Password"
                    type={showPassword ? 'text' : 'password'}
                    variant="transparent"
                    sizeVariant="lg"
                    onFocus={
                      !passwordWatch
                        ? () =>
                            form.setValue(
                              'password',
                              isViewerMode ? 'ViewerPass123!' : guidanceUser.passwordPlaceholder,
                            )
                        : undefined
                    }
                    icon={
                      <Button type="button" size="auto" variant="ghost" onClick={handleToggleShowPass}>
                        <Icons.EyeFilled />
                      </Button>
                    }
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button
            type="submit"
            variant="default"
            className="bg-black hover:bg-slate-800"
            disabled={!usernameWatch || !passwordWatch}
          >
            {isViewerMode ? 'Log In as Viewer' : 'Log In'}
          </Button>
        </form>
      </Form>

      <div className="p-0 text-center justify-center">
        <Link className="underline" to="#">
          Forgot your Password?
        </Link>
      </div>

      <div id="form-nav" className="mt-10 flex flex-wrap gap-x-2 gap-y-5 justify-center">
        {additionalNav.row1.map(({ path, label }, index, arr) => (
          <AdditionalLinkItem key={label} path={path} label={label} withSeparator={index !== arr.length - 1} />
        ))}
        {additionalNav.row2.map(({ path, label }, index, arr) => (
          <AdditionalLinkItem key={label} path={path} label={label} withSeparator={index !== arr.length - 1} />
        ))}
      </div>
    </div>
  );
}

export default FormContent;
